import { createUseStyles } from 'react-jss'
import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { CustomLink } from 'shared/src/api/types'
import { addApi, addUser } from 'shared/src/api'
import { TFunction } from 'i18next'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Modal } from '../Modal'
import { NOTES_BOLD_STYLE, SECONDARY_TITLE_STYLE } from '../styles'
import { IconButton } from '../IconButton'
import { TextFieldNew } from '../TextField'
import { Button } from '../Button'
import { addToastContext } from '../toastContext'
import {
  hasErrors,
  presentStringValidator,
  presentUrlValidator,
  validate,
} from '../../utils/validators'
import { ImageField } from 'components/ImageField'

const SIDE_PADDING = 24

const urlWithScheme = (url: string): string =>
  url.length < 5 || url.indexOf('http') === 0 ? url : `https://${url}`

const validator = (t: TFunction) => ({
  name: presentStringValidator(t('admin.account.userCustomLinks.name'), t),
  url: presentUrlValidator(t('admin.account.userCustomLinks.url'), t),
})

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
    paddingTop: 26,
    height: '100dvh',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  close: {
    position: 'relative',
    right: -10,
    alignSelf: 'center',
  },
  closeIcon: {
    color: '#90734F',
  },
  title: {
    ...SECONDARY_TITLE_STYLE,
    color: '#363432',
    marginBottom: 8,
  },
  description: {
    ...NOTES_BOLD_STYLE,
    color: '#90734F',
    marginBottom: 24,
  },
  button: {
    marginTop: 32,
  },
})

interface Props {
  userId: number
  link?: CustomLink
  newLinkPosition: number
  onCreateOrUpdate: (link: CustomLink) => void
  onCancel: () => void
}

export const CustomLinkModal: FC<Props> = flowMax(
  addDisplayName('CustomLinkModal'),
  addStateHandlers(
    ({ link }) => ({
      name: link?.name,
      url: link?.url,
      imageId: link?.imageId,
    }),
    {
      onChangeName: () => (name: string) => ({ name }),
      onChangeUrl: () => (url: string) => ({ url: urlWithScheme(url) }),
      onChangeImageId: () => (imageId?: number | null) => ({ imageId }),
    }
  ),
  addTranslationHelpers,
  addProps(
    ({ t }) => ({
      validator: validator(t),
    }),
    ['t']
  ),
  addProps(({ validator, name, url }) => ({
    errors: validate(validator, { name, url }),
  })),
  addApi,
  addToastContext,
  addState('hasAttemptedSave', 'setAttemptedSave', false),
  addState('isSaving', 'setSaving', false),
  addHandlers({
    onSave:
      ({
        errors,
        link,
        name,
        url,
        api,
        userId,
        imageId,
        newLinkPosition,
        setAttemptedSave,
        setSaving,
        showSuccessToast,
        showErrorToast,
        onCreateOrUpdate,
        t,
      }) =>
      async () => {
        setAttemptedSave(true)

        if (hasErrors(errors)) {
          return
        }

        setSaving(true)

        const input = {
          userId,
          name: name!,
          url: url!,
          position: link?.position ?? newLinkPosition,
          imageId,
        }

        try {
          var result: CustomLink

          if (link) {
            result = await api.customLinks.update(link.id, input)
            showSuccessToast({
              title: t('admin.account.userCustomLinks.updateSuccessTitle'),
              details: t('admin.account.userCustomLinks.updateSuccessDetails'),
            })
          } else {
            result = await api.customLinks.create(input)
            showSuccessToast({
              title: t('admin.account.userCustomLinks.createSuccessTitle'),
              details: t('admin.account.userCustomLinks.createSuccessDetails'),
            })
          }

          onCreateOrUpdate(result)
        } catch (error) {
          if (link) {
            showErrorToast({
              title: t('admin.account.userCustomLinks.errorTitle'),
              details: t('admin.account.userCustomLinks.errorDetails'),
            })
          }
        } finally {
          setSaving(false)
        }
      },
  }),
  addProps(({ name, url, isSaving }) => ({
    saveDisabled: !name || !url || isSaving,
  })),
  addUser,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    errors,
    link,
    onCancel,
    name,
    onChangeName,
    url,
    imageId,
    onChangeUrl,
    onChangeImageId,
    onSave,
    hasAttemptedSave,
    saveDisabled,
    user: { isAdmin },
    classes,
    t,
  }) => (
    <Modal containerClassName={classes.container}>
      <div className={classes.header}>
        <IconButton
          name="close"
          onClick={onCancel}
          className={classes.close}
          iconClassName={classes.closeIcon}
          addHitSlop
        />
      </div>
      <h2 className={classes.title}>
        {t('admin.account.userCustomLinks.editTitle')}
      </h2>
      <p className={classes.description}>
        {t('admin.account.userCustomLinks.editDescription')}
      </p>
      <TextFieldNew
        label={t('admin.account.userCustomLinks.name')}
        inputProps={{ maxLength: 20 }}
        value={name}
        onChange={onChangeName}
        error={hasAttemptedSave ? errors.name : undefined}
      />
      <TextFieldNew
        label={t('admin.account.userCustomLinks.url')}
        inputProps={{ type: 'url' }}
        value={url}
        onChange={onChangeUrl}
        error={hasAttemptedSave ? errors.url : undefined}
      />
      {isAdmin && (
        <ImageField
          id="imageId"
          label={t('admin.resources.users.fields.imageId')}
          value={imageId}
          onChange={onChangeImageId}
        />
      )}
      <Button
        type="fullWidth"
        text={
          link
            ? t('admin.account.userCustomLinks.update')
            : t('admin.account.userCustomLinks.create')
        }
        onClick={onSave}
        disabled={saveDisabled}
        className={classes.button}
      />
    </Modal>
  )
)
